import { trackEvent } from "../../services/analytics";
import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { userVerify } from "../../store/actions/userVerifyActions";
import { resetVerifyState } from "../../store/reducers/userVerifyReducers";
import { endKioskSession } from "../../services/kioskServiceSession";

const CODE_LENGTH = 4;

// Раскладка клавиатуры: цифры + «стереть» и «подтвердить» в нижнем ряду
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "del", "0", "ok"];

const blockVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, delay: i * 0.12 },
    }),
};

function VerifyUser() {
    const [code, setCode] = useState(Array(CODE_LENGTH).fill(""));
    const [deviceToken, setDeviceToken] = useState("");
    // Таймер бездействия держим в ref: в state он попадал бы в зависимости
    // эффекта и заставлял бы его переподписывать слушатели на каждый тик.
    const sessionTimeoutRef = useRef(null);
    // Редирект вперёд должен происходить только после реальной отправки кода,
    // иначе при возврате «Назад» на этот экран (юзер уже залогинен) эффект
    // сработает на маунте и мгновенно выбросит обратно в список.
    const hasSubmittedRef = useRef(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userData, loading, error } = useSelector((state) => state.verify);
    const { t } = useTranslation();

    // Reset error state on component mount
    useEffect(() => {
        dispatch(resetVerifyState()); // Reset the verify state to clear error
    }, [dispatch]);

    // Получение deviceToken and other logic
    useEffect(() => {
        // Из localStorage
        const storedDeviceToken = localStorage.getItem("deviceToken");
        if (storedDeviceToken) {
            setDeviceToken(storedDeviceToken);
        }

        // Через postMessage от Electron
        const handleMessage = (event) => {
            if (
                event.origin === "https://nurtest.space" &&
                event.data.type === "DEVICE_TOKEN"
            ) {
                setDeviceToken(event.data.token);
                localStorage.setItem("deviceToken", event.data.token);
            }
        };
        window.addEventListener("message", handleMessage);

        // Через Electron API
        if (window.electronAPI) {
            window.electronAPI.getDeviceToken().then((token) => {
                setDeviceToken(token);
                localStorage.setItem("deviceToken", token);
            });
        }

        // Таймер бездействия (5 минут)
        const resetSession = () => {
            if (sessionTimeoutRef.current) {
                clearTimeout(sessionTimeoutRef.current);
            }
            sessionTimeoutRef.current = setTimeout(
                () => {
                    endKioskSession();
                    setCode(Array(CODE_LENGTH).fill(""));
                    navigate("/");
                },
                5 * 60 * 1000,
            );
        };

        // События для сенсорного интерфейса
        window.addEventListener("touchstart", resetSession);

        return () => {
            window.removeEventListener("touchstart", resetSession);
            window.removeEventListener("message", handleMessage);
            if (sessionTimeoutRef.current) {
                clearTimeout(sessionTimeoutRef.current);
            }
        };
    }, [navigate]);

    const handleNumberClick = (number) => {
        const newCode = [...code];
        const firstEmptyIndex = newCode.findIndex((digit) => digit === "");
        if (firstEmptyIndex !== -1) {
            newCode[firstEmptyIndex] = number;
            setCode(newCode);
        }
    };

    const handleClear = () => {
        setCode((prevCode) => {
            const newCode = [...prevCode];
            // Находим индекс последней заполненной цифры
            let lastFilledIndex = -1;
            for (let i = newCode.length - 1; i >= 0; i--) {
                if (newCode[i] !== "") {
                    lastFilledIndex = i;
                    break;
                }
            }
            if (lastFilledIndex !== -1) {
                newCode[lastFilledIndex] = "";
            }
            return newCode;
        });
    };

    const handleSubmit = () => {
        const fullCode = code.join("");
        if (fullCode.length === CODE_LENGTH && deviceToken) {
            trackEvent("login_attempt");
            hasSubmittedRef.current = true;
            dispatch(userVerify({ code: fullCode, deviceToken }))
                .then((result) => {
                    trackEvent("login_result", { status: result.payload?.access_token ? "success" : "error" });
                    if (result.payload?.access_token) {
                        navigate("/printing/show_list/user/");
                    }
                })
                .catch((error) => {});
        }
    };

    useEffect(() => {
        if (hasSubmittedRef.current && userData && userData.user_id) {
            navigate(`/printing/show_list/user/${userData.user_id}`);
        }
    }, [userData, navigate]);

    // Курсор стоит на первой пустой ячейке; когда код набран — курсора нет.
    const currentIndex = code.findIndex((digit) => digit === "");

    return (
        <div className="h-[88vh] px-[2.4vw] py-[2vh] flex flex-col gap-[2vh] bg-[#EDEFF4]">
            {/* Заголовок и краткая памятка */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={0}
                className="flex-none bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] p-[3vh_2.4vw] flex items-center gap-[2.8vw]"
            >
                <h2 className="flex-1 text-[2.9vh] font-extrabold tracking-tight leading-tight">
                    {t("verifyUser.title")}
                </h2>
                <div className="flex-[1.1] flex flex-col gap-[1.4vh]">
                    {[1, 2, 3].map((n) => (
                        <div
                            key={n}
                            className="flex items-center gap-[1.2vw] text-[1.5vh] font-semibold"
                        >
                            <span className="w-[2.8vh] h-[2.8vh] flex-none rounded-full bg-[#F1F3F7] text-[#7B8290] text-[1.25vh] font-bold flex items-center justify-center">
                                {n}
                            </span>
                            <span>{t(`verifyUser.hint${n}`)}</span>
                        </div>
                    ))}
                </div>
            </motion.div>

            {/* Ячейки кода и строка статуса */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={1}
                className="flex-none flex flex-col items-center gap-[1.4vh]"
            >
                <div className="flex gap-[2.2vw]">
                    {code.map((digit, index) => (
                        <div
                            key={index}
                            className={`w-[13.9vw] h-[9.2vh] rounded-[2.4vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] flex items-center justify-center text-[5vh] font-extrabold border-[0.3vh] transition-colors ${
                                index === currentIndex
                                    ? "border-[#246CE2]"
                                    : "border-transparent"
                            } ${digit ? "bg-[#E3ECFB]" : "bg-white"}`}
                        >
                            {digit}
                        </div>
                    ))}
                </div>
                {/* Высота зарезервирована, чтобы статус не двигал вёрстку */}
                <div className="h-[2.6vh] text-[1.4vh] font-bold text-center">
                    {error ? (
                        <span className="text-[#C2543E]">
                            {t("verifyUser.error")}: {error}
                        </span>
                    ) : (
                        <span className="text-[#7B8290]">
                            {loading
                                ? t("verifyUser.loading")
                                : t("verifyUser.code_valid")}
                        </span>
                    )}
                </div>
            </motion.div>

            {/* Экранная цифровая клавиатура */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={2}
                className="flex-1 min-h-0 bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] p-[2vh_2vw] grid grid-cols-3 auto-rows-fr gap-[1vh]"
            >
                {KEYS.map((key) => {
                    if (key === "del") {
                        return (
                            <button data-analytics="verifyuser.handleClear.1"
                                key={key}
                                onClick={handleClear}
                                disabled={loading}
                                aria-label={t("verifyUser.clear")}
                                className="rounded-[2vh] bg-[#F1F3F7] text-[#12161F] flex items-center justify-center active:bg-[#E4E7EE] disabled:opacity-50"
                            >
                                <svg
                                    className="w-[2.4vh] h-[2.4vh]"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M21 5H8l-5 7 5 7h13a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1z" />
                                    <path d="M12 9l6 6M18 9l-6 6" />
                                </svg>
                            </button>
                        );
                    }

                    if (key === "ok") {
                        return (
                            <button data-analytics="verifyuser.handleSubmit.2"
                                key={key}
                                onClick={handleSubmit}
                                disabled={loading}
                                aria-label={t("verifyUser.submit")}
                                className="rounded-[2vh] bg-[#246CE2] text-white flex items-center justify-center active:bg-[#1B55B4] disabled:opacity-50"
                            >
                                <svg
                                    className="w-[2.4vh] h-[2.4vh]"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#fff"
                                    strokeWidth="2.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M4 12.5l5 5L20 6.5" />
                                </svg>
                            </button>
                        );
                    }

                    return (
                        <button
                            key={key}
                            onClick={() => handleNumberClick(key)}
                            disabled={loading}
                            className="rounded-[2vh] bg-[#F1F3F7] text-[#12161F] text-[2.8vh] font-bold flex items-center justify-center active:bg-[#E4E7EE] disabled:opacity-50"
                        >
                            {key}
                        </button>
                    );
                })}
            </motion.div>
        </div>
    );
}

export default VerifyUser;
